'use client';

/* eslint-disable max-len */
import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowUp, ChevronRight } from 'lucide-react';
import { cn } from '@/util/cn';
import { sendMessageToAiAssistant } from '@/util/sendMessageToAiAssistant';
import { useSize } from '@/util/useSize';
import { getLocale } from '@/i18n/locale';
import { getPromptsByTemplateId } from '@/util/getPromptsByTemplateId';
import { ELocale } from '@/i18n/localeEnum';
import { SkiGptLogo } from '@/components/common/SkiGptLogo';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// In-page SkiGPT promotion, EN_US only (the component renders null on
// every other locale, so call sites don't gate it themselves). Mirrors
// the home-hero treatment: a bordered
// "card" with a solid-white SkiGPT sticker chip half-overlapping the
// top edge so the brand reads cleanly without a competing heading row.
// Every breakpoint renders the chat textarea + suggestion pills +
// ArrowUp submit. Mobile used to collapse to just the pills, which left
// the card barely taller than the chip and gave phone visitors no way to
// ask their own question — only the two canned prompts. It now carries
// the same input (on a taller 3rem floor, so it reads as a real ask box
// and clears the iOS tap target), still capped to two pills so the row
// clears the submit button.

interface SkiGptPromotionProps {
  name?: string;
  templateId?: string;
  suggestions?: string[];
  className?: string;
}

const fadeGradientClass = 'bg-[linear-gradient(90deg,hsl(var(--background)/0)_0%,hsl(var(--background)/0.82)_42%,hsl(var(--background))_72%)]';

const pillClass = [
  'whitespace-nowrap rounded-full bg-white/60 px-3 py-1',
  'text-xs font-medium text-muted-foreground',
  'transition hover:bg-white hover:text-secondary hover:shadow-sm',
].join(' ');

export const SkiGptPromotion = ({
  name,
  templateId,
  suggestions: manualSuggestions = [],
  className,
}: SkiGptPromotionProps) => {
  const { t } = useOTSTranslation('common');
  const [inputValue, setInputValue] = useState('');
  const [shouldScrollSuggestions, setShouldScrollSuggestions] = useState(false);
  const [showSuggestionsFade, setShowSuggestionsFade] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const locale = getLocale() as ELocale;
  const isSupportedLocale = locale === ELocale.EN_US;

  const suggestions = useMemo(() => {
    if (!isSupportedLocale) return [];
    if (manualSuggestions.length > 0) return manualSuggestions;
    if (templateId) return getPromptsByTemplateId(templateId, name) || [];
    return [];
  }, [isSupportedLocale, manualSuggestions, templateId, name]);

  const suggestionsSize = useSize(suggestionsRef);

  if (!isSupportedLocale) {
    return null;
  }

  useLayoutEffect(() => {
    const textareaElement = textareaRef.current;
    if (!textareaElement) return;

    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const maxHeight = 6 * rootFontSize;

    textareaElement.style.height = 'auto';
    textareaElement.style.height = `${Math.min(textareaElement.scrollHeight, maxHeight)}px`;
    const shouldScroll = textareaElement.scrollHeight > maxHeight;

    textareaElement.style.overflowY = shouldScroll ? 'auto' : 'hidden';

    if (shouldScroll) {
      requestAnimationFrame(() => {
        textareaElement.scrollTo({ top: textareaElement.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [inputValue]);

  useLayoutEffect(() => {
    const suggestionsElement = suggestionsRef.current;
    if (!suggestionsElement) return;

    const suggestionButtons = Array.from(suggestionsElement.children) as HTMLElement[];
    if (!suggestionButtons.length) {
      setShouldScrollSuggestions(false);
      return;
    }

    const { columnGap } = getComputedStyle(suggestionsElement);
    const gap = Number.parseFloat(columnGap) || 0;
    const totalSuggestionsWidth = suggestionButtons.reduce((total, button) => (
      total + button.offsetWidth
    ), 0) + (gap * Math.max(suggestionButtons.length - 1, 0));

    setShouldScrollSuggestions(totalSuggestionsWidth > suggestionsElement.clientWidth);
  }, [suggestions, suggestionsSize?.width]);

  useLayoutEffect(() => {
    const suggestionsElement = suggestionsRef.current;
    if (!suggestionsElement || !shouldScrollSuggestions) {
      setShowSuggestionsFade(false);
      return undefined;
    }

    const updateFadeVisibility = () => {
      setShowSuggestionsFade(suggestionsElement.scrollLeft <= 1);
    };

    updateFadeVisibility();
    suggestionsElement.addEventListener('scroll', updateFadeVisibility, { passive: true });

    return () => suggestionsElement.removeEventListener('scroll', updateFadeVisibility);
  }, [shouldScrollSuggestions, suggestions, suggestionsSize?.width]);

  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessageToAiAssistant(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (text: string) => {
    sendMessageToAiAssistant(text);
  };

  const placeholder = name
    ? t('aiAssistant.callout.placeholderWithName', { name })
    : t('aiAssistant.callout.placeholder');

  return (
    <div className={cn('relative', className)}>
      {/* py-1.5 + a 20px lockup keeps the chip at 32px tall, so -top-4 still
          centres it on the card's top border. */}
      <div className="absolute -top-4 left-1/2 z-10 inline-flex -translate-x-1/2 items-center rounded-full bg-white px-4 py-1.5 shadow-md ring-1 ring-black/5 md:left-6 md:translate-x-0">
        <SkiGptLogo className="h-5" />
      </div>

      <div className="rounded-2xl border border-border p-3 pt-6 md:p-4 md:pt-6">
        <textarea
          ref={textareaRef}
          className={cn(
            'block min-h-[3rem] max-h-24 w-full resize-none md:min-h-[2.25rem]',
            'border-0 bg-transparent px-2 py-1',
            'text-base leading-snug text-foreground',
            'placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0',
          )}
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={450}
          rows={1}
        />

        <div className="mt-2 flex items-end justify-between gap-3 md:mt-1">
          {suggestions.length > 0 ? (
            <div className="relative min-w-0 flex-1 overflow-hidden">
              <div
                ref={suggestionsRef}
                className={cn(
                  'flex min-w-0 gap-2',
                  shouldScrollSuggestions
                    ? 'flex-nowrap overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                    : 'flex-wrap',
                  'max-md:[&>*:nth-child(n+3)]:hidden',
                )}
              >
                {suggestions.map((text) => (
                  <button
                    key={text}
                    type="button"
                    onClick={() => handleSuggestionClick(text)}
                    className={cn(pillClass, shouldScrollSuggestions && 'shrink-0')}
                  >
                    {text}
                  </button>
                ))}
              </div>
              <div
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute inset-y-0 -right-px flex w-24 items-center justify-end pr-0.5',
                  fadeGradientClass,
                  'after:absolute after:inset-y-0 after:right-0 after:w-6 after:bg-background',
                  'transition-opacity',
                  showSuggestionsFade ? 'opacity-100' : 'opacity-0',
                )}
              >
                <ChevronRight className="relative z-10 h-6 w-auto text-primary" />
              </div>
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <button
            type="button"
            onClick={handleSend}
            aria-label={t('aiAssistant.callout.sendAlt')}
            disabled={!inputValue.trim()}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-secondary/90 disabled:bg-secondary/40"
          >
            <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};
